import { useLingui } from '@lingui/react/macro';
import { type FC, memo, useMemo } from 'react';
import { useSettings } from '@/settings/api';
import { DisplayQuoteToggleGroup } from '@/settings/DisplayQuoteToggleGroup';
import { useCoin } from '@/shared/api';
import { Digits } from '@/shared/Digits';
import { Widget } from '@/shared/Widget';

type CoinMarketDataSectionProps = {
  slug?: string;
};

export const CoinMarketDataSection: FC<CoinMarketDataSectionProps> = memo(
  ({ slug }) => {
    const { t } = useLingui();
    const { data: coin } = useCoin(slug?.toUpperCase());
    const market = coin?.market_data;
    const [settings] = useSettings();

    const range = useMemo(() => {
      if (!market) {
        return null;
      }
      const low = market.low_24h;
      const high = market.high_24h;
      const price = market.price;
      const denominator = high - low || 1;
      const progress = (price - low) / denominator;
      return {
        label: t`Price Range (24h)`,
        low: settings.displayQuote === 'usdt' ? low : market.low_24h_iqd,
        high: settings.displayQuote === 'usdt' ? high : market.high_24h_iqd,
        progress: Math.min(Math.max(progress, 0), 1),
      };
    }, [market, t, settings]);

    const metrics = useMemo(() => {
      if (!market) return [];
      return [
        {
          key: 'ath',
          label: t`ATH`,
          value: (
            <Digits
              value={
                settings.displayQuote === 'usdt'
                  ? market.ath?.toString()
                  : market.ath_iqd?.toString()
              }
              coin={settings.displayQuote}
              abbreviate
              className="text-base font-normal"
            />
          ),
        },
        {
          key: 'change-1h',
          label: t`Price Change (1h)`,
          value: (
            <Digits
              value={market.change_1h?.toString() ?? '0'}
              color="auto"
              sign="always"
              unit="%"
              className="text-base font-normal"
            />
          ),
        },
        {
          key: 'change-24h',
          label: t`Price Change (24h)`,
          value: (
            <Digits
              value={market.change_24h?.toString() ?? '0'}
              sign="always"
              unit="%"
              color="auto"
              className="text-base font-normal"
            />
          ),
        },
        {
          key: 'change-7d',
          label: t`Price Change (7d)`,
          value: (
            <Digits
              value={market.change_7d?.toString() ?? '0'}
              unit="%"
              color="auto"
              sign="always"
              className="text-base font-normal"
            />
          ),
        },
        {
          key: 'market-cap',
          label: t`Market Cap`,
          value: (
            <Digits
              value={market.market_cap?.toString()}
              coin="USDT"
              abbreviate
              className="text-base font-normal"
            />
          ),
        },
        {
          key: 'volume',
          label: t`24h Volume`,
          value: (
            <Digits
              value={market.volume_24h?.toString()}
              abbreviate
              coin="USDT"
              className="text-base font-normal"
            />
          ),
        },
        {
          key: 'circulating',
          label: t`Circulating Supply`,
          value: (
            <Digits
              value={market.circulating_supply}
              className="text-base font-normal text-content-primary"
              abbreviate
            />
          ),
        },
        {
          key: 'max-supply',
          label: t`Max Supply`,
          value: (
            <Digits
              value={market.max_supply}
              className="text-base font-normal text-content-primary"
              abbreviate
            />
          ),
        },
      ];
    }, [market, t, settings]);

    return (
      <Widget loading={!market}>
        <div className="mb-4 flex items-center justify-between">
          <p className="text-lg font-medium text-content-primary">{t`Market Data`}</p>
          <DisplayQuoteToggleGroup size={8} variant="button" />
        </div>

        {range && (
          <div className="mb-8">
            <p className="text-xs text-content-tertiary mb-2">{range.label}</p>
            <div className="h-3 bg-border-disabled rounded-full relative mb-2">
              <div
                className="absolute top-3.5 -translate-y-1/2 h-4 w-2 rounded-full bg-content-primary transition-transform"
                style={{
                  left: `${range.progress * 100}%`,
                  transform: 'translate(-50%, -50%)',
                }}
              />
            </div>
            <div className="flex items-center justify-between text-xs text-content-tertiary">
              <span>
                {t`24h Low`}{' '}
                <Digits
                  value={range.low?.toString()}
                  coin={settings.displayQuote}
                  className="text-xs"
                />
              </span>
              <span>
                {t`24h High`}{' '}
                <Digits
                  value={range.high?.toString()}
                  coin={settings.displayQuote}
                  className="text-xs"
                />
              </span>
            </div>
          </div>
        )}

        <div className="grid grid-cols-2 desktop:grid-cols-4 gap-4">
          {metrics.map((metric) => (
            <div key={metric.key} className=" rounded-xl p-4 space-y-1">
              <p className="text-xs text-content-tertiary">{metric.label}</p>
              {metric.value}
            </div>
          ))}
        </div>
      </Widget>
    );
  },
);
